/**
 * Pure helper functions for Excel logbook parsing and validation.
 * Shared between the CLI script (scripts/migrate-excel.js) and the
 * browser-side XLS import (src/components/settings/xls-import.js).
 *
 * This module contains NO Node-specific imports (no fs, path, url, etc.)
 * so it can be safely bundled by Vite for the browser.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FUEL_LEVEL_MAP = {
  'full':  'FULL',
  '3/4':   'THREE_QUARTER',
  '2/3':   'TWO_THIRD',
  '1/2':   'HALF',
  '1/3':   'ONE_THIRD',
  '1/4':   'QUARTER',
  'empty': 'EMPTY',
};

/**
 * Default column mapping: Excel column letter → field name.
 */
export const DEFAULT_COL_MAPPING = {
  // Existing columns (F–T)
  F: 'flight_date',
  G: 'aircraft',
  H: 'pilot',
  I: 'departure',
  J: 'destination',
  K: 'block_time_mins',
  L: 'flight_time_mins',
  M: 'main_tank_level',
  N: 'right_wing_level',
  O: 'belly_tank_level',
  P: 'notes',
  R: 'fuel_uplift',
  T: 'oil_level',
  // New fields
  Q: 'left_wing_level',
  S: 'fuel_account_type',
  U: 'departure_time',
  V: 'arrival_time',
  W: 'ifr_time_mins',
  X: 'takeoffs',
  Y: 'landings',
  Z: 'who_pays',
  AA: 'p2_name',
  AB: 'purpose',
  AC: 'fuel_uplift_cost_currency',
};

// ---------------------------------------------------------------------------
// Pure parsing / mapping / validation functions
// ---------------------------------------------------------------------------

/**
 * Parse a time-of-day value into an "HH:MM" string.
 *
 * Accepted forms:
 *   "09:15"           → "09:15"   (HH:MM string)
 *   "9:15"            → "09:15"   (H:MM string)
 *   0.3854 (number)   → "09:15"   (Excel time serial, fraction of 24h)
 *   null / "" / ws    → null
 *   "25:00" / "abc"   → null      (out of range or unparseable)
 */
export function parseTimeOfDay(value) {
  if (value == null) return null;

  if (typeof value === 'number') {
    if (isNaN(value) || value < 0 || value >= 1) return null;
    const totalMins = Math.round(value * 1440);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  const s = String(value).trim();
  if (s === '') return null;

  // Try HH:MM or H:MM first (before numeric, because parseFloat("00:30")
  // returns 0 which would incorrectly match the Excel serial path)
  const match = s.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const mins = parseInt(match[2], 10);
    if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }

  // Try as numeric Excel serial
  const num = parseFloat(s);
  if (!isNaN(num) && num >= 0 && num < 1) {
    const totalMins = Math.round(num * 1440);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  return null;
}

/**
 * Parse a cell value as a non-negative integer.
 * Returns the integer or null for invalid / negative / non-integer values.
 */
export function parsePositiveInt(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (s === '') return null;
  const num = Number(s);
  if (!Number.isFinite(num) || num < 0 || !Number.isInteger(num)) return null;
  return num;
}

/**
 * Parse a cell value against a set of allowed enum values (case-insensitive).
 * Returns the normalised uppercase string if matched, null otherwise.
 *
 * @param {*} value        Raw cell value
 * @param {string[]} allowedValues  Array of uppercase allowed strings
 */
export function parseEnum(value, allowedValues) {
  if (value == null) return null;
  const s = String(value).trim().toUpperCase();
  if (s === '') return null;
  return allowedValues.includes(s) ? s : null;
}

/**
 * Parse a cell value as a 3-letter alphabetic currency code (e.g. "GBP").
 * Returns the uppercase code or null.
 */
export function parseCurrencyCode(value) {
  if (value == null) return null;
  const s = String(value).trim().toUpperCase();
  if (s === '' || s.length !== 3 || !/^[A-Z]{3}$/.test(s)) return null;
  return s;
}

/**
 * Trim whitespace from a cell value and return the string, or null for
 * empty / whitespace-only / null / undefined values.
 *
 * @param {*}       value               Raw cell value
 * @param {boolean} [collapseWhitespace=false]  Collapse internal whitespace to single space
 */
export function parseFreeText(value, collapseWhitespace = false) {
  if (value == null) return null;
  let s = String(value).trim();
  if (s === '') return null;
  if (collapseWhitespace) s = s.replace(/\s+/g, ' ');
  return s;
}

/**
 * Parse a time value (string or number) to integer minutes.
 * Returns NaN for unparseable values.
 *
 * Accepted forms:
 *   "1:30"            → 90    (h:mm string)
 *   "1.5"             → 90    (decimal-hours string)
 *   1.5  (number ≥1)  → 90    (decimal hours)
 *   0.0118 (number <1) → 17   (Excel time fraction-of-day)
 *   ""                → NaN
 */
export function parseTimeToMins(str) {
  if (str == null) return NaN;

  if (typeof str === 'number') {
    if (isNaN(str) || str < 0) return NaN;
    if (str < 1) return Math.round(str * 1440);
    return Math.round(str * 60);
  }

  const s = String(str).trim();
  if (s === '') return NaN;

  if (s.includes(':')) {
    const parts = s.split(':');
    if (parts.length !== 2) return NaN;
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(mins) || mins < 0 || mins > 59) return NaN;
    return hours * 60 + mins;
  }

  const num = parseFloat(s);
  if (isNaN(num) || num < 0) return NaN;
  return Math.round(num * 60);
}

/**
 * Map fuel level text to Fuel_Level_Enum value.
 * Returns null for unrecognised or empty values.
 */
export function mapFuelLevel(text, levelMap = FUEL_LEVEL_MAP) {
  if (text == null) return null;
  const key = String(text).trim().toLowerCase();
  if (key === '') return null;
  return Object.hasOwn(levelMap, key) ? levelMap[key] : null;
}

/**
 * Normalise a pilot name: trim, collapse internal whitespace.
 */
export function normalisePilotName(name) {
  if (name == null) return '';
  return String(name).trim().replace(/\s+/g, ' ');
}

/**
 * Transform a raw Excel row object (keyed by column letter) into a flight
 * payload object using the provided column mapping.
 */
export function mapExcelRow(row, colMapping = DEFAULT_COL_MAPPING, fuelLevelMap = FUEL_LEVEL_MAP, opts = {}) {
  const mapped = {};
  const pilotAliases = opts.pilotAliases || null;
  const sourceRefCol = opts.sourceRefCol || null;

  if (sourceRefCol && row[sourceRefCol] != null && row[sourceRefCol] !== '') {
    mapped.source_ref = String(row[sourceRefCol]).trim();
  }

  for (const [col, field] of Object.entries(colMapping)) {
    const raw = row[col];

    switch (field) {
      case 'flight_date':
        mapped.flight_date = raw != null ? raw : null;
        break;

      case 'aircraft':
        mapped.aircraft = raw != null ? String(raw).trim() : '';
        break;

      case 'pilot': {
        const normalised = normalisePilotName(raw);
        if (pilotAliases && normalised) {
          const key = normalised.toLowerCase();
          mapped.pilot = Object.hasOwn(pilotAliases, key) ? pilotAliases[key] : normalised;
        } else {
          mapped.pilot = normalised;
        }
        break;
      }

      case 'departure':
      case 'destination':
        mapped[field] = raw != null ? String(raw).trim() : '';
        break;

      case 'block_time_mins':
      case 'flight_time_mins':
        mapped[field] = parseTimeToMins(raw);
        break;

      case 'main_tank_level':
      case 'right_wing_level':
      case 'belly_tank_level':
      case 'left_wing_level':
        mapped[field] = mapFuelLevel(raw, fuelLevelMap);
        break;

      case 'notes':
        mapped.notes = raw != null ? String(raw).trim() : '';
        break;

      case 'fuel_uplift': {
        if (raw != null && raw !== '') {
          const s = String(raw).trim();
          const litresMatch = s.match(/([\d.]+)\s*[Ll]?/);
          const costMatch = s.match(/[£$]([\d.]+)/);
          mapped.fuel_uplift_litres = litresMatch ? parseFloat(litresMatch[1]) : null;
          mapped.fuel_uplift_cost = costMatch ? parseFloat(costMatch[1]) : null;
        } else {
          mapped.fuel_uplift_litres = null;
          mapped.fuel_uplift_cost = null;
        }
        break;
      }

      case 'oil_level': {
        const val = raw != null ? parseFloat(String(raw).trim()) : NaN;
        mapped.oil_level = isNaN(val) ? null : val;
        break;
      }

      case 'departure_time':
      case 'arrival_time':
        mapped[field] = parseTimeOfDay(raw);
        break;

      case 'ifr_time_mins': {
        const mins = parseTimeToMins(raw);
        mapped[field] = isNaN(mins) ? null : mins;
        break;
      }

      case 'takeoffs':
      case 'landings':
        mapped[field] = parsePositiveInt(raw);
        break;

      case 'who_pays':
        mapped[field] = parseEnum(raw, ['CAPTAIN', 'P2']);
        break;

      case 'p2_name':
        mapped[field] = parseFreeText(raw, true);
        break;

      case 'purpose':
        mapped[field] = parseFreeText(raw);
        break;

      case 'fuel_account_type':
        mapped[field] = parseEnum(raw, ['ACCOUNT', 'OVERSEAS_ACCOUNT', 'NON_ACCOUNT']);
        break;

      case 'fuel_uplift_cost_currency':
        mapped[field] = parseCurrencyCode(raw);
        break;

      default:
        mapped[field] = raw;
    }
  }

  return mapped;
}

/**
 * Validate a mapped Excel row. Returns { valid, errors }.
 */
export function validateExcelRow(mapped) {
  const errors = [];

  if (mapped.flight_date == null || mapped.flight_date === '') {
    errors.push('Missing or empty flight_date');
  }

  if (!mapped.aircraft) {
    errors.push('Missing or empty aircraft name');
  }

  if (!mapped.pilot) {
    errors.push('Missing or empty pilot name');
  }

  if (isNaN(mapped.block_time_mins) || mapped.block_time_mins <= 0) {
    errors.push('block_time_mins must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Convert an Excel serial date number to a YYYY-MM-DD string.
 * If the value is already a string that looks like a date, return it as-is.
 */
export function excelSerialToDate(serial) {
  if (serial == null) return null;

  if (typeof serial === 'string') {
    const trimmed = serial.trim();
    if (trimmed === '') return null;
    if (/\d{4}[-/]\d{2}[-/]\d{2}/.test(trimmed)) {
      return trimmed.replace(/\//g, '-');
    }
    const num = parseFloat(trimmed);
    if (isNaN(num)) return trimmed;
    serial = num;
  }

  if (typeof serial !== 'number' || isNaN(serial)) return null;

  const epoch = new Date(Date.UTC(1899, 11, 30));
  const date = new Date(epoch.getTime() + serial * 86400000);
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
